// ============================================================================
// FSA NOTE — SHARED NUMERIC INPUT
// ============================================================================
// Text-based controlled numeric input shared by Main Note + disclosure
// grids. Solves the well-known `<input type="number">` controlled-state
// quirks with React:
//
//   - Leading "0" can be cleared (string draft state, not number state)
//   - Typing "-" alone works (commits the negative on first digit / blur)
//   - Trailing decimals don't fight the controlled value (draft persists
//     until blur, then normalises)
//   - External value changes still propagate (effect resyncs draft when
//     parent updates didn't originate from this input)
//
// Commits to parent on every valid keystroke AND on blur. Empty / partial
// drafts like ""/ "-" / "." commit as 0.

import React from 'react';

interface Props {
  value: number;
  onChange: (n: number) => void;
  readOnly?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  title?: string;
  placeholder?: string;
}

export const NumericInput: React.FC<Props> = ({
  value,
  onChange,
  readOnly,
  disabled,
  style,
  className,
  title,
  placeholder,
}) => {
  const [draft, setDraft] = React.useState<string>(() => formatNumber(value));
  const lastCommittedRef = React.useRef<number>(value);

  React.useEffect(() => {
    if (value !== lastCommittedRef.current) {
      setDraft(formatNumber(value));
      lastCommittedRef.current = value;
    }
  }, [value]);

  const validDraftRe = /^-?\d*\.?\d*$/;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (!validDraftRe.test(raw)) return;
    setDraft(raw);
    const parsed = raw === '' || raw === '-' || raw === '.' || raw === '-.'
      ? 0
      : parseFloat(raw);
    if (!Number.isNaN(parsed)) {
      lastCommittedRef.current = parsed;
      onChange(parsed);
    }
  };

  const handleBlur = () => {
    const parsed = parseFloat(draft);
    const n = Number.isNaN(parsed) ? 0 : parsed;
    lastCommittedRef.current = n;
    setDraft(formatNumber(n));
    if (n !== value) onChange(n);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      value={draft}
      readOnly={readOnly}
      disabled={disabled}
      style={style}
      title={title}
      placeholder={placeholder}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );
};

/** Format a number for display in NumericInput. Empty when 0 so the
 *  placeholder / clean look stays; otherwise full precision (parseFloat
 *  reconstructs without trailing zeros). */
export function formatNumber(n: number): string {
  if (n === 0) return '';
  return String(n);
}

export default NumericInput;
